import { useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/router'
import { BiCloudDownload } from 'react-icons/bi'
import classNames from 'classnames'
import { RiCloseFill } from 'react-icons/ri'
import { BsArrowLeft } from 'react-icons/bs'
import { useDispatch, useSelector } from 'react-redux'
import { getFilters, setFilters } from '@/store/slices/speakersSlice'
import { setProfileSeo } from '@/store/slices/headSlice'
import { toastState } from '@/utils/Toast.util'
import axios from 'axios'
import { request } from '@/utils/axiosUtil'
import Loader from '@/components/common/Loader'
import Button from '@/components/common/Button'
import SpeechTitles from '@/components/speaker/SpeechTitles'
import FaqAndInfo from '@/components/dashboard/FaqAndInfo'
import Categories from '@/components/speaker/Categories'
import Media from '@/components/speaker/Media'
import SpeechTitlesModal from '@/components/speaker/SpeechTitlesModal'
import Toast from '@/components/common/Toast'
import SpeakerDetails from '@/components/speaker/SpeakerDetails'
import RelatedSpeakers from '@/components/speaker/RelatedSpeakers'
import BestKnown from '@/components/speaker/BestKnown'
import LatestNews from '@/components/speaker/LatestNews'
import { Popover } from '@headlessui/react'
import styles from '@/styles/components/speaker.module.scss'

const Profile = (props) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const filters = useSelector(getFilters)
  const loggedInUserDetails = JSON.parse(localStorage.getItem('userDetails')) || {}
  const [isSpeechTitlesOpen, setSpeechTitlesOpen] = useState(false)
  const [userBookmarks, setUserBookmarks] = useState(null)
  const [speakerData, setSpeakerData] = useState(null)
  const [isSpeakerBookmarked, setSpeakerBookmarked] = useState(false)
  const [isToast, setIsToast] = useState(false)
  const [toastContent, setToastContent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDownloadingProfile, setIsDownloadingProfile] = useState(false)
  const [isSpeakersEnabled, setSpeakersEnabled] = useState(false)
  const [isInternalUser, setInternalUser] = useState(false)
  const [userDetails, setUserDetails] = useState({})
  const [speakerList, setSpeakerList] = useState([])

  const bookmarkedIDs = []

  const fetchJson = async (apiUrl) => {
    const res = await request({ url: apiUrl })
    if (res?.status == 200) {
      return res?.data
    } else {
      setIsLoading(false)
      router.push(`/${res?.status}`)
      return res?.response?.data
    }
  }
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('userDetails'))
    if (user) setUserDetails(user)
  }, [])

  useEffect(() => {
    if (router.isReady && router.query.slug) {
      getSpeakerProfileData()
    }
  }, [router.query])

  useEffect(() => {
    if (loggedInUserDetails?.access_token) {
      setInternalUser(loggedInUserDetails?.userDetails?.isInternalUser)
      getAllBookMarks()
      setSpeakersEnabled(true)
    }
  }, [loggedInUserDetails?.access_token])

  userBookmarks?.map((item, i) => {
    bookmarkedIDs[i] = item.slug
  })

  useEffect(() => {
    setSpeakerBookmarked(bookmarkedIDs?.includes(speakerData?.[0]?.slug))
  }, [userBookmarks, bookmarkedIDs, speakerData])

  const speakerCategories = []
  const speechTopics = { headings: [], descriptions: [], speechTitles: [] }
  speakerData?.[0]?.speakersTopics?.map((topic, i) => {
    speakerCategories[i] = { id: topic.id, name: topic?.name, slug: topic?.slug }
  })

  const getAllBookMarks = async () => {
    const url = '/bookmark'
    const res = await request({ url: url })
    if (res?.status == 200) {
      setUserBookmarks(res?.data?.data)
    }
  }

  const handleAddBookmark = async (id) => {
    const body = {
      bookmark: id,
    }
    const url = '/bookmark'
    const res = await request({ url: url, method: 'POST', data: JSON.stringify(body) })
    if (res?.data?.success) {
      setToastContent(toastState('ADDBOOKMARK', { type: 'Added' }))
      setIsToast(true)
      getAllBookMarks()
    } else if (res?.response?.status == 500) {
      router.push(`/${res?.response?.status}`)
    } else if (res?.response?.data?.statusCode == 409) {
      router.push('/409')
    }
  }

  const handleDeleteBookmark = async (id) => {
    const body = {
      bookmark: id,
    }
    const url = '/bookmark'
    const res = await request({ url: url, method: 'DELETE', data: JSON.stringify(body) })
    if (res?.data?.success) {
      setToastContent(toastState('REMOVEBOOKMARK', { type: 'Removed' }))
      setIsToast(true)
      getAllBookMarks()
    } else if (res?.response?.status == 500) {
      router.push(`/${res?.response?.status}`)
    } else if (res?.response?.data?.statusCode == 409) {
      router.push('/409')
    }
  }

  const handleRequestBooking = (slug) => {
    const url = `/book-now`
    router.push(url)
  }

  const fetchData = async () => {
    const previewflag = router.query.preview ? 'preview=true' : ''
    const url = `${process.env.NEXT_PUBLIC_APIM_BASE_PATH}/downloadprofile` + '?' + previewflag
    const payload = { slug: speakerData[0].slug }
    const data = await fetch(url, {
      method: 'POST',
      headers: {
        'Ocp-Apim-Subscription-Key': process.env.NEXT_PUBLIC_APIM_SUBSCRIPTION_KEY,
        'Content-Type': 'application/json',
        Authorization: `Bearer ${loggedInUserDetails?.access_token}`,
      },
      body: JSON.stringify(payload),
    })
    if (data?.status == 201) {
      // convert the response into an array Buffer
      return data.arrayBuffer()
    } else {
      router.push(`/${data?.status}`)
    }
  }

  const downloadPdf = async () => {
    setIsDownloadingProfile(true)
    const buffer = await fetchData()
    setIsDownloadingProfile(false)
    if (buffer) {
      const blob = new Blob([buffer])
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${speakerData[0].name} Speaker Profile.pdf`
      link.click()
    }
  }

  const makeRelatedSpeakers = useCallback(
    (url, _speakerList = speakerList) => {
      const index = _speakerList && _speakerList?.findIndex((x) => x.slug === url)
      let notIArray = []
      const beforeI = _speakerList && _speakerList?.slice(0, index)
      const afterI = _speakerList && _speakerList?.slice(index + 1)

      if (beforeI?.length == 0) {
        return afterI?.length >= 4 ? afterI?.slice(0, 4) : afterI
      } else if (afterI?.length == 0) {
        return beforeI?.length >= 4 ? beforeI?.slice(0, 4) : beforeI
      } else if (afterI?.length >= 4) {
        return afterI?.slice(0, 4)
      }
      notIArray = [...afterI, ...beforeI]
      return notIArray?.length >= 4 ? notIArray?.slice(0, 4) : notIArray
    },
    [speakerList]
  )

  const getSpeakersList = useCallback(
    async ({
      _searchValue = filters?.searchQuery || '',
      _selectedCategories = filters?.categories || '',
      _selectedSpeakerTypes = filters?.types || '',
      _relatedSpeakers = filters?.relatedSpeakers,
      _sortOrder = filters?.sortOrder,
    } = {}) => {
      if (_relatedSpeakers) {
        setIsLoading(true)
        const result = await fetchJson(
          `${process.env.NEXT_PUBLIC_APIM_BASE_PATH}/speakers?query=${encodeURIComponent(
            _searchValue
          )}&categories=${_selectedCategories?.toString()}&type=${_selectedSpeakerTypes?.toString()}&order=${
            _sortOrder?.value
          }&limit=40`
        )
        if (result?.data) {
          setSpeakerList(result?.data)
          return result?.data
        }
      }
    },
    [filters?.searchQuery, filters?.categories, filters?.types, filters?.relatedSpeakers]
  )

  const getSpeakerProfileData = useCallback(async () => {
    setIsLoading(true)
    try {
      const tempSpeakerList = await getSpeakersList()
      const slug = typeof router.query.slug == 'string' ? router.query?.slug : router.query?.slug[0]

      const transformedData = {
        ...props?.transformedData,
        relatedSpeakers:
          props?.transformedData?.relatedSpeakers?.length == 0 &&
          filters?.relatedSpeakers &&
          filters?.relatedSpeakers.length > 1
            ? makeRelatedSpeakers(slug, tempSpeakerList)
            : props?.transformedData?.relatedSpeakers,
      }

      dispatch(setProfileSeo(props?.transformedData?.seoTags))
      setSpeakerData([transformedData])
      setTimeout(() => {
        setIsLoading(false)
      }, 1500)
    } catch (err) {
      router.push(`/${err?.response?.status}`)
    }
  }, [router?.query])

  return (
    <>
      {isLoading || isDownloadingProfile ? (
        <Loader />
      ) : (
        <div className="-mt-1 border-hidden">
          <div className="uta-container">
            <div className="py-6 maxSm:pt-[22px]">
              <div className="flex items-center justify-between py-4 maxSm:pt-0">
                <Popover className="relative h-5 hover:border-b">
                  <Popover.Button
                    className="flex"
                    onClick={() => {
                      dispatch(
                        setFilters({
                          ...filters,
                          profileCategory: null,
                          relatedSpeakers: null,
                          sortOrder: null,
                        })
                      )
                      router.push('/speakers')
                    }}
                  >
                    <BsArrowLeft className="text-xl" />
                    <p className="inline-block pl-2 text-sm font-medium regular-font">
                      Search Results
                    </p>
                  </Popover.Button>
                </Popover>

                {isInternalUser && (
                  <div>
                    <Button
                      className="flex gap-2 text-sm"
                      label={'Download Profile'}
                      handleClick={downloadPdf}
                      icon={<BiCloudDownload className="text-xl" />}
                    ></Button>
                  </div>
                )}
              </div>
              <div>
                {speakerData?.map((speaker, i) => (
                  <SpeakerDetails
                    speaker={speaker}
                    isSpeakersEnabled={isSpeakersEnabled}
                    isSpeakerBookmarked={isSpeakerBookmarked}
                    setSpeakerBookmarked={setSpeakerBookmarked}
                    handleDeleteBookmark={handleDeleteBookmark}
                    handleAddBookmark={handleAddBookmark}
                    handleRequestBooking={handleRequestBooking}
                    setSpeechTitlesOpen={setSpeechTitlesOpen}
                    loggedInUserDetails={userDetails}
                    key={i}
                  />
                ))}

                {isToast && (
                  <Toast
                    variant={toastContent?.variant}
                    message={toastContent?.message}
                    handleClick={() => setIsToast(false)}
                  />
                )}

                {/* Media starts here  */}
                {speakerData?.[0].video?.length > 0 && <Media videoData={speakerData?.[0].video} />}
                {/* Media ends here  */}
                {speakerData?.[0]?.popularProjects && (
                  <div
                    className={classNames(
                      !speakerData?.[0]?.video?.length > 0
                        ? 'lg:mt-14'
                        : 'lg:px-0 pb-8 maxSm:pb-5 w-full mt-20 maxSm:mt-6'
                    )}
                  >
                    <BestKnown speakerData={speakerData} />
                  </div>
                )}
                {speakerData?.[0]?.articles?.length !== 0 && (
                  <LatestNews speakerData={speakerData} />
                )}
                <div className="my-0 border-t-[1px] border-custom-gray-80 w-full"></div>
                {speakerCategories.length !== 0 && (
                  <div className="my-14">
                    <Categories
                      label="Topics"
                      categories={speakerCategories}
                      speakerData={speakerData}
                    />
                  </div>
                )}
                {speakerData?.[0]?.relatedSpeakers?.length !== 0 && (
                  <div className="border-t-[1px] border-custom-gray-80 w-full"></div>
                )}
              </div>
            </div>
          </div>
          <div className="browse-speakers-container">
            {speakerData && speakerData?.length > 0 && (
              <RelatedSpeakers
                speakerData={speakerData}
                setIsLoading={setIsLoading}
                makeRelatedSpeakers={makeRelatedSpeakers}
              />
            )}
          </div>
          <FaqAndInfo />
        </div>
      )}
      {isSpeechTitlesOpen && (
        <SpeechTitlesModal
          name="speechtitles"
          header={
            <div className={`${styles.readMore} text-3xl text-white uppercase tagline-font`}>
              {speakerData?.[0]?.name}
            </div>
          }
          info={
            <>
              {speakerData?.[0]?.speechTopics && (
                <div className="inline-block mr-8 xl:block xl:mt-0 lg:mt-10 md:mt-6 maxLg:order-2">
                  <SpeechTitles
                    viewAll={true}
                    label="Speech Titles"
                    titlesHeading={speakerData?.[0]?.speechTopics}
                    titlesDescription={
                      speechTopics.descriptions ? speechTopics.descriptions : ['-']
                    }
                    outSideClick={() => setSpeechTitlesOpen(false)}
                  />
                </div>
              )}
            </>
          }
          footer={
            <>
              <button
                className="flex justify-center px-4 py-2 border w-36 button-hover"
                onClick={() => {
                  setSpeechTitlesOpen(false)
                }}
              >
                <Button className="text-sm font-semibold" label="Close"></Button>
              </button>
            </>
          }
          closeIcon={
            <>
              <button
                className="absolute right-[0] bg-black text-white cursor-pointer maxSm:pl-3"
                onClick={() => {
                  setSpeechTitlesOpen(false)
                }}
              >
                <RiCloseFill className="w-6 h-6" />
              </button>
            </>
          }
          outSideClick={() => setSpeechTitlesOpen(false)}
        />
      )}
    </>
  )
}
async function getSpeakerProfile(userDetails, getParams) {
  try {
    const apiUrl = `${process.env.NEXT_PUBLIC_APIM_BASE_PATH}/speakerProfile`
    const config = {
      headers: {
        'Ocp-Apim-Subscription-Key': process.env.NEXT_PUBLIC_APIM_SUBSCRIPTION_KEY,
        Authorization: `Bearer ${userDetails?.access_token}`,
      },
      params: getParams(),
    }
    const data = await axios.get(apiUrl, config)

    return data
  } catch (err) {
    return err
  }
}

export async function getServerSideProps(context) {
  var userDetails = ''
  if (typeof window !== 'undefined') {
    userDetails = JSON.parse(localStorage.getItem('userDetails'))
  }
  const slug =
    typeof context?.query?.slug == 'string' ? context?.query?.slug : context?.query?.slug[0]
  const getParams = () => (context?.query?.preview ? { slug: slug, preview: true } : { slug: slug })
  const { data } = (await getSpeakerProfile(userDetails, getParams)) ?? null

  if (data?.data?.length === 0 || !data?.success) {
    return {
      notFound: true,
    }
  }
  const speakerData = data?.data[0]
  const speechTopicsTemp = speakerData?.speechTopics
    ? speakerData?.speechTopics.slice(0, 10)
    : speakerData?.speechTopics
  const video = speakerData?.video ? speakerData?.video.slice(0, 10) : speakerData?.video
  const popularProjects = speakerData?.popularProjects
  const articles = speakerData?.articles
    ? speakerData?.articles.slice(0, 10)
    : speakerData?.articles
  const transformedData = {
    ...speakerData,
    speechTopics: speechTopicsTemp,
    video: video,
    popularProjects: popularProjects,
    articles: articles,
  }

  return { props: { transformedData } }
}
export default Profile
